'use client';

import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import type { MatchedGame, Sport } from '@rfqsports/shared';
import { useStore } from '@/lib/store';
import { send } from '@/lib/ws-client';
import { cn } from '@/lib/cn';
import { parseSportFilter, SPORT_LABEL } from '@/lib/sport';
import { SportSidebar } from './sport-sidebar';
import { LobbyToolbar } from './lobby-toolbar';
import { DaySection } from './day-section';

interface Props {
  lockedSport?: Sport;
}

const LIVE_WINDOW_MS = 3 * 3600_000;
const MAX_LOBBY_SUBSCRIPTIONS = 60;

export function Lobby(props: Props): JSX.Element {
  return (
    <Suspense fallback={<LobbySkeleton />}>
      <LobbyInner {...props} />
    </Suspense>
  );
}

function LobbyInner({ lockedSport }: Props): JSX.Element {
  const games = useStore((s) => s.games);
  const matchedAt = useStore((s) => s.matchedAt);
  const connected = useStore((s) => s.connected);
  const params = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sportFilter = lockedSport ?? parseSportFilter(params.get('sport')) ?? 'all';
  const statusFilter = parseStatus(params.get('status')) ?? 'upcoming';
  const query = (params.get('q') ?? '').trim().toLowerCase();

  const filtered = useMemo(() => {
    const now = Date.now();
    return games
      .filter((g) => {
        if (sportFilter !== 'all' && g.sport !== sportFilter) return false;
        if (statusFilter === 'live') {
          if (g.startTs > now || g.startTs < now - LIVE_WINDOW_MS) return false;
        } else if (statusFilter === 'upcoming') {
          if (g.startTs < now) return false;
        }
        if (query) {
          const blob = `${g.homeTeam} ${g.awayTeam} ${g.league}`.toLowerCase();
          if (!blob.includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => a.startTs - b.startTs);
  }, [games, sportFilter, statusFilter, query]);

  useLobbySubscriptions(connected, filtered.slice(0, MAX_LOBBY_SUBSCRIPTIONS).map((g) => g.id));

  const dayGroups = useMemo(() => groupByDay(filtered), [filtered]);
  const heading = lockedSport ? sportHeading(lockedSport) : 'Sports';

  return (
    <div className="flex flex-col gap-4">
      <MobileSportToggle onOpen={() => setDrawerOpen(true)} />
      <div className="grid gap-6 md:grid-cols-[200px_1fr] lg:grid-cols-[220px_1fr]">
        <div className="hidden md:block">
          <SportSidebar />
        </div>
        <div className="flex flex-col gap-5">
          <header className="flex items-end justify-between gap-3">
            <h1 className="text-2xl font-bold text-text">{heading}</h1>
            {matchedAt ? (
              <p className="text-[11px] text-muted">
                Matched {new Date(matchedAt).toLocaleTimeString()} · refreshes every 5 min
              </p>
            ) : null}
          </header>
          <LobbyToolbar />
          {!games.length ? (
            <EmptyState matchedAt={matchedAt} />
          ) : !filtered.length ? (
            <p className="rounded-lg border border-border bg-panel py-10 text-center text-sm text-muted">
              No games match the current filters.
            </p>
          ) : (
            <div className="flex flex-col gap-8">
              {dayGroups.map((g) => (
                <DaySection key={g.label} label={g.label} games={g.games} />
              ))}
            </div>
          )}
        </div>
      </div>
      <SportDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}

function MobileSportToggle({ onOpen }: { onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center justify-between rounded-md border border-border bg-panel px-3 py-2 text-sm text-text md:hidden"
    >
      <span className="flex items-center gap-2">
        <span aria-hidden>☰</span>
        Sport filter
      </span>
      <span className="text-muted">›</span>
    </button>
  );
}

function SportDrawer({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-30 flex md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Sport filter"
    >
      <button
        type="button"
        aria-label="Close sport filter"
        className="flex-1 bg-bg/70"
        onClick={onClose}
      />
      <div className={cn('w-72 bg-panel border-l border-border p-3 overflow-y-auto')}>
        <div className="flex items-center justify-between pb-2">
          <span className="text-sm font-semibold text-text">Filter games</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm text-muted hover:text-text"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <SportSidebar onClose={onClose} />
      </div>
    </div>
  );
}

function EmptyState({ matchedAt }: { matchedAt: number }): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-panel p-8 text-center text-sm text-muted">
      <p>No games available yet.</p>
      <p className="mt-2 text-xs">
        The matcher pulls fresh events from Polymarket and Kalshi every 5 minutes.
        {matchedAt ? ` Last attempt: ${new Date(matchedAt).toLocaleTimeString()}.` : ''}
      </p>
    </div>
  );
}

function LobbySkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-6 md:grid-cols-[200px_1fr] lg:grid-cols-[220px_1fr]">
        <div className="hidden md:block">
          <div className="h-40 rounded-lg border border-border bg-panel animate-pulse" />
        </div>
        <div className="flex flex-col gap-4">
          <div className="h-7 w-32 rounded bg-panel animate-pulse" />
          <div className="h-9 w-full rounded bg-panel animate-pulse" />
          <div className="h-40 rounded-lg border border-border bg-panel animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function parseStatus(v: string | null): 'live' | 'upcoming' | 'all' | null {
  if (v === 'live' || v === 'upcoming' || v === 'all') return v;
  return null;
}

function sportHeading(sport: Sport): string {
  return SPORT_LABEL[sport] ?? 'Sports';
}

const DAY_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

function groupByDay(games: MatchedGame[]): { label: string; games: MatchedGame[] }[] {
  const buckets = new Map<string, MatchedGame[]>();
  for (const g of games) {
    const label = DAY_FMT.format(new Date(g.startTs));
    const arr = buckets.get(label) ?? [];
    arr.push(g);
    buckets.set(label, arr);
  }
  return [...buckets.entries()].map(([label, games]) => ({ label, games }));
}

/**
 * Subscribes the visible games for live odds; mirrors the old
 * useLobbySubscriptions in matched-game-list.tsx so the cards aren't blank.
 * The detail page's own subscribe dedupes via the adapter set.
 */
function useLobbySubscriptions(connected: boolean, visibleIds: string[]): void {
  const subscribed = useRef<Set<string>>(new Set());
  const key = visibleIds.join('|');

  useEffect(() => {
    if (!connected) return;
    const next = new Set(visibleIds);
    const prev = subscribed.current;
    for (const id of next) if (!prev.has(id)) send({ t: 'subscribe_game', gameId: id });
    for (const id of prev) if (!next.has(id)) send({ t: 'unsubscribe_game', gameId: id });
    subscribed.current = next;
  }, [connected, key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      for (const id of subscribed.current) send({ t: 'unsubscribe_game', gameId: id });
      subscribed.current.clear();
    };
  }, []);
}
